import React, { useEffect } from 'react';
import { ToggleLayer, useHover, Transition } from 'react-laag';
import usePrevious from '../../hooks/usePrevious';
import Box from '../Box/Box';
import { Caption } from '../Typography/Typography';
import { theme } from '../../../config/theme';
import { rem } from 'polished';

type TooltipProps = React.ComponentProps<typeof ToggleLayer> & {
    children: React.ReactNode;
    content: React.ReactNode;
    open?: boolean;
    onHoverToggle?: (n) => void;
    disabled?: boolean;
};

const Tooltip = ({
    children,
    content,
    open = false,
    onHoverToggle,
    disabled,
    ...props
}: TooltipProps) => {
    const [show, hoverProps] = useHover({ delayEnter: 400, delayLeave: 300 });
    const prevShow = usePrevious(show);

    useEffect(() => {
        if (onHoverToggle && show !== prevShow) onHoverToggle(show);
    }, [onHoverToggle, prevShow, show]);

    return (
        <ToggleLayer
            isOpen={(open || show) && !disabled}
            renderLayer={({ isOpen, layerProps }) => (
                <Transition isOpen={isOpen}>
                    {(isOpen, onTransitionEnd) => (
                        <div
                            ref={layerProps.ref}
                            onTransitionEnd={onTransitionEnd}
                            style={{
                                ...layerProps.style,
                                transition: 'opacity 0.15s ease',
                                opacity: isOpen ? 1 : 0,
                                maxWidth: '300px',
                                color: theme.colors.neutral.white,
                                zIndex: 999999,
                            }}
                        >
                            {typeof content === 'function'
                                ? content(isOpen)
                                : content}
                        </div>
                    )}
                </Transition>
            )}
            {...props}
            placement={{
                anchor: props.placement?.anchor,
                triggerOffset: props.placement?.triggerOffset || 10,
            }}
        >
            {({ triggerRef }) => (
                <span
                    ref={triggerRef}
                    {...hoverProps}
                    style={{ display: 'inline-flex' }}
                >
                    {children}
                </span>
            )}
        </ToggleLayer>
    );
};

const Content = ({ children }) => (
    <Box
        bg={theme.colors.primary[1800]}
        borderRadius={theme.radii.sm}
        paddingY={rem(4)}
        paddingX={rem(8)}
        display="flex"
        alignItems="center"
    >
        <Caption light>{children}</Caption>
    </Box>
);

Tooltip.Content = Content;

Tooltip.defaultProps = {
    placement: {
        anchor: 'BOTTOM_CENTER',
        triggerOffset: 10,
    },
};

export default Tooltip;
